import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AppLayout from '~/components/appLayout'
import { LatLngBoundsLiteral, LatLngLiteral } from '~/components/map/mapPosition'
import MapboxMap from '~/components/mapbox/map'
import { truthy } from '~/prix'
import ChartCluster from '~/components/mapbox/chartCluster'
import useAction from '~/prix/react/hooks/action'
import useAPI from '~/prix/react/hooks/api'
import fetchLegalEntityAttendanceSizeDefinition from './queries/fetchLegalEntityAttendanceSize/fetchLegalEntityAttendanceSize.definition'
import { useDebounce } from 'react-use'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ScreenDefault } from '~/components/baseInterface'
import { getDefaultTimeForVariable } from '~/packages/variable/recommendedLayerForVariable.util'
import { Layer, TimeIncidence } from '~/packages/variable/spaceTime.types'
import { VariableWithConclusion } from '~/packages/variable/variables.typing'
import viewListDefinition from '~/packages/view/list/viewList.definition'
import { legalEntitySizeMapTool } from './legalEntitySize.tools'
import { formatDate, parseDate } from '~/prix/types/date'
import bboxPolygon from '@turf/bbox-polygon'
import booleanWithin from '@turf/boolean-within'
import union from '@turf/union'
import type { Feature, MultiPolygon, Polygon } from 'geojson'
import { maskCnpj } from '~/prix/utils/cnpj'
import { sizeColors } from '~/design'
import LegalEntitySizeLegend, {
  LegalEntitySizeLegendKey,
  legalEntitySizeLegendOrder,
} from './legalEntitySizeLegend.component'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import LegalEntityReadOne from '../legalEntity/read/legalEntityReadOne.component'
import Modal from '~/prix/react/components/modal'
import type { Expression } from '~/prix/types/expression'

const LoadingWrapper = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(12, 35, 64, 0.35);
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  z-index: 3;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transition: opacity 0.3s;
`

interface LegalEntitySizeLocationState {
  currentBaseLayer?: Layer
}

type LegalEntitySizeRequestState = {
  key: string
  input: {
    bounds: LatLngBoundsLiteral | null
    zoom: number | undefined
    time: TimeIncidence
    filter?: Expression | null
  }
}

type LegalEntitySizeClusterItem = {
  position: LatLngLiteral
  color: string
  label?: string
  type?: string
  description: string
  weight?: number
  h3?: string
  value?: number
  id?: string
}

type LegalEntitySizeGridMetadata = {
  id: number
  bounds: Feature<Polygon> | Polygon
  h3Resolution: number
  shouldCluster: boolean
}

type LegalEntitySizeResultMetadata = {
  grids?: LegalEntitySizeGridMetadata[]
  gridSize?: number | null
  h3Resolution?: number | null
  shouldCluster?: boolean | null
  zoomRange?: { min: number; max: number } | null
  startDate?: string | null
  endDate?: string | null
}

/**
 * Verifica se os bounds estão dentro de uma coleção de polígonos.
 * @param bounds Bounds a ser verificado.
 * @param grids Coleção de polígonos a ser verificado.
 * @returns True, se a geometria do bound estiver dentro da coleção de geometria dos grids, false se não estiver.
 */
function isBoundsInsideLoadedGridCoverage(
  bounds: LatLngBoundsLiteral,
  grids: LegalEntitySizeGridMetadata[],
) {
  const loadedGridCoverage = grids.length
    ? grids.reduce<Feature<Polygon | MultiPolygon> | null>((coverage, grid) => {
        const gridFeature =
          grid.bounds.type === 'Feature'
            ? grid.bounds
            : {
                type: 'Feature',
                properties: {},
                geometry: grid.bounds,
              }

        if (!coverage) {
          return gridFeature
        }

        return (union as any)(coverage, gridFeature) ?? coverage
      }, null)
    : null

  if (!loadedGridCoverage) {
    return false
  }

  const currentBounds = bboxPolygon([bounds.west, bounds.south, bounds.east, bounds.north])

  return booleanWithin(currentBounds, loadedGridCoverage)
}

/**
 * Verifica se o resultado atual pode ser reutilizado nos novos bounds.
 * @param result Resultado mais recente da chamada de api do mapa clusterizado.
 * @param bounds Geometria dos bounds, visão atual do usuário.
 * @param zoom Zoom atual do usuário em mapa.
 * @param time Filtro temporal (quando) do usuário.
 * @returns True, se os novos bounds, zoom e filtros não precisarem gerar uma nova requisição, false se precisa de novos dados.
 */
function canReuseGridResult(
  result: any,
  bounds: LatLngBoundsLiteral,
  zoom: number | undefined,
  time: TimeIncidence,
) {
  const metadata = result?.metadata as LegalEntitySizeResultMetadata | undefined

  if (!metadata || !time.startDate || !time.endDate) {
    return false
  }

  const resolvedZoom = zoom ?? 4

  const gridLevel = {
    gridSize: resolvedZoom <= 4 ? 5 : 1,
    shouldCluster: resolvedZoom < 12,
  }

  return (
    metadata.startDate === time.startDate &&
    metadata.endDate === time.endDate &&
    metadata.gridSize === gridLevel.gridSize &&
    metadata.shouldCluster === gridLevel.shouldCluster &&
    metadata.zoomRange &&
    metadata.zoomRange.min &&
    resolvedZoom <= metadata.zoomRange.max &&
    isBoundsInsideLoadedGridCoverage(bounds, metadata.grids || [])
  )
}

function getDefaultLegalEntitySizeLayer(): Layer {
  return {
    variable: legalEntitySizeMapTool,
    space: {
      delimitationType: 'country',
      delimitationId: 30,
      delimitationTitle: 'Brasil',
      childrenType: 'item',
      childrenTitle: 'Agrupamentos',
      childrenItemView: 'cluster',
    },
    time: getDefaultTimeForVariable(legalEntitySizeMapTool as any),
  } as Layer
}

function normalizeLegalEntitySizeLayer(layer?: Layer | null): Layer {
  if (layer?.variable?.key !== legalEntitySizeMapTool.key) {
    return getDefaultLegalEntitySizeLayer()
  }

  return {
    ...layer,
    variable: {
      ...legalEntitySizeMapTool,
      ...layer.variable,
    },
  } as Layer
}

function normalizeLegalEntitySizeLegendKey(rawSize: string | undefined): LegalEntitySizeLegendKey {
  const normalized = String(rawSize ?? '')
    .trim()
    .toUpperCase()

  if (normalized === 'MEI' || normalized === 'ME' || normalized === 'EPP') {
    return normalized
  }

  return 'Others'
}

function isPointInsideBounds(position: LatLngLiteral, currentBounds: LatLngBoundsLiteral) {
  const isLatitudeInside =
    position.lat >= currentBounds.south && position.lat <= currentBounds.north

  const isLongitudeInside =
    currentBounds.west <= currentBounds.east
      ? position.lng >= currentBounds.west && position.lng <= currentBounds.east
      : position.lng >= currentBounds.west || position.lng <= currentBounds.east

  return isLatitudeInside && isLongitudeInside
}

export default function LegalEntitySizeScreen() {
  const { context, isLoading: isLoadingAPI } = useAPI()

  const location = useLocation()
  const navigate = useNavigate()
  const locationState = location.state as LegalEntitySizeLocationState | null

  const [bounds, setBounds] = useState<LatLngBoundsLiteral | null>(null)
  const [zoom, setZoom] = useState<number | undefined>(4)
  const [debouncedBounds, setDebouncedBounds] = useState<LatLngBoundsLiteral | null>(null)
  const [debouncedZoom, setDebouncedZoom] = useState<number | undefined>(4)

  const [requestState, setRequestState] = useState<LegalEntitySizeRequestState | null>(null)
  const isFirstBoundsChange = useRef(true)
  const [modalInformationId, setModalInformationId] = useState<string | null>(null)

  const layer = useMemo(
    () => normalizeLegalEntitySizeLayer(locationState?.currentBaseLayer),
    [locationState?.currentBaseLayer],
  )

  const currentTime = layer.time as TimeIncidence
  const currentFilter = (
    layer.variable as typeof legalEntitySizeMapTool & {
      filter?: Expression | null
    }
  ).filter
  const currentFilterKey = useMemo(() => JSON.stringify(currentFilter ?? null), [currentFilter])

  const variablesResult = useAction(viewListDefinition, {}, [], {
    autoLoad: Boolean(context.user) && !isLoadingAPI,
    staleWhileRevalidate: true,
  })
  const variables = (variablesResult.result?.items ?? []) as VariableWithConclusion[]

  const handleChangeLayer = useCallback(
    (nextLayer: Layer | ((oldLayer: Layer) => Layer)) => {
      const resolvedLayer = typeof nextLayer === 'function' ? nextLayer(layer) : nextLayer
      const selectedPath = (resolvedLayer.variable as { path?: string }).path

      if (resolvedLayer.variable.key === legalEntitySizeMapTool.key) {
        navigate('.', {
          replace: false,
          state: {
            currentBaseLayer: resolvedLayer,
          },
        })
        return
      }

      if (!selectedPath) {
        navigate('/app/sentinel', {
          state: {
            baseLayers: [resolvedLayer],
          },
        })
        return
      }
    },
    [layer, navigate],
  )

  useDebounce(
    () => {
      setDebouncedBounds(bounds)
      setDebouncedZoom(zoom)
    },
    300,
    [bounds],
  )

  const {
    result: gridResult,
    isLoading,
    callAction: reloadLegalEntityAttendanceSize,
  } = useAction(
    fetchLegalEntityAttendanceSizeDefinition,
    requestState?.input ?? {},
    [requestState?.key],
    {
      autoLoad: Boolean(requestState),
      staleWhileRevalidate: true,
    },
  )

  useEffect(() => {
    if (!debouncedBounds || !currentTime.startDate || !currentTime.endDate || isLoading) {
      return
    }

    const boundsKey = [
      debouncedBounds.west,
      debouncedBounds.south,
      debouncedBounds.east,
      debouncedBounds.north,
    ]
      .map(value => value.toFixed(6))
      .join(',')

    const key = [
      boundsKey,
      debouncedZoom ?? '',
      currentTime.startDate ?? '',
      currentTime.endDate ?? '',
      currentFilterKey,
    ].join('|')

    if (
      requestState?.key === key &&
      canReuseGridResult(gridResult, debouncedBounds, debouncedZoom, currentTime)
    ) {
      return
    }

    setRequestState(currentRequestState => {
      if (currentRequestState?.key === key) {
        return currentRequestState
      }

      return {
        key,
        input: {
          bounds: debouncedBounds,
          zoom: debouncedZoom,
          time: currentTime,
          filter: currentFilter ?? null,
        },
      }
    })
  }, [
    debouncedBounds,
    debouncedZoom,
    currentFilter,
    currentFilterKey,
    currentTime,
    currentTime.startDate,
    currentTime.endDate,
    gridResult,
    requestState?.key,
  ])

  const itemsResolved = useMemo<LegalEntitySizeClusterItem[] | null>(() => {
    if (!gridResult) return null

    const { items } = gridResult
    //TODO rever se é possível usar os metadados pra revalidar a necessidade de um recálculo. Ex, todos os tiles da grid nos metadados já existem na visualização atual.

    const result = items
      .map((item: any) => {
        const coordinates = (item.coordinates as any)?.coordinates
        if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
          return null
        }

        const description = [
          `Porte: ${item.size || 'Unknown'}`,
          item.ageGroup ? `Maturidade: ${item.ageGroup}` : null,
          item.cnpj ? `CNPJ: ${maskCnpj(item.cnpj)}` : null,
          item.tradeName ? `Nome fantasia: ${item.tradeName}` : null,
          item.corporateName ? `Razão social: ${item.corporateName}` : null,
          item.openingDate
            ? `Data de abertura: ${formatDate(parseDate(String(item.openingDate)))}`
            : null,
          item.cnaeDescription ? `CNAE: ${item.cnaeDescription}` : null,
          item.segmentDescription ? `Segmento: ${item.segmentDescription}` : null,
        ]
          .filter(truthy)
          .join('\n')

        return {
          position: {
            lng: coordinates[0],
            lat: coordinates[1],
          } as LatLngLiteral,
          color: sizeColors[item.size as keyof typeof sizeColors] || '#000',
          label: item.tradeName ?? item.corporateName ?? (item.size as string) ?? undefined,
          type: (item.size as string) ?? undefined,
          description: description,
          weight: item.weight,
          h3: item.h3,
          value: item.weight,
          id: item.id == null ? undefined : String(item.id),
        }
      })
      .filter(truthy)
    return result
  }, [JSON.stringify(gridResult)])

  const visibleItems = useMemo(() => {
    if (!itemsResolved) {
      return []
    }

    if (!bounds) {
      return itemsResolved
    }

    return itemsResolved.filter(item => isPointInsideBounds(item.position, bounds))
  }, [bounds, itemsResolved])

  const visibleTotalsBySize = useMemo<Record<LegalEntitySizeLegendKey, number>>(() => {
    const totals = legalEntitySizeLegendOrder.reduce(
      (accumulator, size) => {
        accumulator[size] = 0
        return accumulator
      },
      {} as Record<LegalEntitySizeLegendKey, number>,
    )

    visibleItems.forEach(item => {
      const sizeKey = normalizeLegalEntitySizeLegendKey(item.type)
      const value = Number(item.weight ?? item.value ?? 0)

      if (Number.isFinite(value)) {
        totals[sizeKey] += value
      }
    })

    return totals
  }, [visibleItems])

  const visibleTotal = useMemo(() => {
    return legalEntitySizeLegendOrder.reduce(
      (sum, sizeKey) => sum + visibleTotalsBySize[sizeKey],
      0,
    )
  }, [visibleTotalsBySize])

  const position = useMemo(() => ({ lat: -15.7939, lng: -47.8828 }), [])

  const boundsChangedEvent = useCallback((newBounds: LatLngBoundsLiteral) => {
    if (isFirstBoundsChange.current) {
      isFirstBoundsChange.current = false
    }

    setBounds(newBounds)
  }, [])

  const zoomChangedEvent = useCallback((newZoom: number | undefined) => {
    setZoom(newZoom)
  }, [])

  const handleClickCluster = useCallback(
    async (id: number, coordinates: [number, number], zoom?: number) => {
      if (!bounds || !currentTime.startDate || !currentTime.endDate) {
        return
      }

      reloadLegalEntityAttendanceSize({
        bounds,
        zoom,
        time: currentTime,
        filter: currentFilter ?? null,
        idCluster: id,
      })
    },
    [bounds, currentFilter, currentTime, reloadLegalEntityAttendanceSize],
  )

  const handleClickItem = useCallback(
    (item: { id?: string | number | null }) => {
      if (!item.id) {
        return
      }

      setModalInformationId(String(item.id))
    },
    [setModalInformationId],
  )

  return (
    <AppLayout
      dockActive={'sentinel'}
      isLoading={isLoading && !isFirstBoundsChange}
      title='Porte das Empresas'
      isFloating={true}
    >
      <div style={{ height: '100dvh', width: '100%', position: 'relative' }}>
        <LoadingWrapper $isVisible={isLoading || variablesResult.isLoading}>
          <PulseLoaderComponent customColor='#ffffff' />
        </LoadingWrapper>
        <MapboxMap
          center={position}
          zoom={4}
          onBoundsChanged={boundsChangedEvent}
          onZoomEnd={zoomChangedEvent}
        >
          {itemsResolved && (
            <ChartCluster
              items={itemsResolved}
              clusterRadius={60}
              clusterMaxZoom={13}
              fontFamily="'Figtree', Arial, sans-serif"
              onClusterClick={handleClickCluster}
              onItemClick={handleClickItem}
            />
          )}
        </MapboxMap>

        <ScreenDefault.ToolControls
          variables={variables}
          layer={layer}
          onChangeLayer={handleChangeLayer}
          stateId={null}
          stateName={null}
          moreInfo={
            <LegalEntitySizeLegend
              visibleTotal={visibleTotal}
              visibleTotalsBySize={visibleTotalsBySize}
              unitPlural={layer.variable.unitPlural}
            />
          }
        />
      </div>

      {/* Modal com o ReadOne */}
      <Modal
        isOpen={!!modalInformationId}
        onClose={() => setModalInformationId(null)}
        modalStyle='sentinel'
        baseWidth='80vw'
        baseHeight='80vh'
        scrollY={true}
        hideCloseButton={true}
      >
        {modalInformationId && (
          <LegalEntityReadOne
            id={modalInformationId}
            onClose={() => setModalInformationId(null)}
            isSentinel
          />
        )}
      </Modal>
    </AppLayout>
  )
}
